import React, { useState, useRef } from "react";

/**
 * LazyImage — componente de carga perezosa con técnica "blur-up".
 *
 * Técnicas implementadas:
 * 1. Placeholder oscuro/pulsante mientras carga
 * 2. loading="lazy" para lazy loading nativo del navegador
 * 3. Transición suave (opacity) al cargar la imagen real
 * 4. Aspect ratio explícito para evitar saltos de layout (CLS)
 * 5. Detección de ReactSnap para evitar hydration mismatch
 *
 * @param {object} props
 * @param {string} props.src        — URL de la imagen
 * @param {string} props.alt        — Alt text
 * @param {string} props.className  — Clases adicionales para el <img>
 * @param {string} props.containerClassName — Clases para el contenedor
 * @param {string} props.aspectRatio — Aspect ratio (ej: "4/5", "1/1", "auto")
 * @param {string} props.objectFit  — object-fit (cover/contain)
 * @param {function} props.onLoad   — Callback cuando la imagen carga
 */
const isReactSnap = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

export default function LazyImage({
  src,
  srcSet,
  sizes,
  alt,
  className = "",
  containerClassName = "",
  aspectRatio = "auto",
  objectFit = "cover",
  priority = false,
  onLoad,
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // Durante el pre-renderizado de ReactSnap, renderizar solo el placeholder
  // para evitar hydration mismatch con IntersectionObserver y load events.
  if (isReactSnap) {
    return (
      <div
        className={`relative overflow-hidden ${containerClassName}`}
        style={{ aspectRatio }}
      >
        <div className="absolute inset-0 bg-[#120F0D]">
          <div className="w-full h-full bg-gradient-to-br from-[#1A1512] to-[#120F0D]" />
        </div>
      </div>
    );
  }

  // Eager loading for priority (Hero) images — precarga inmediata sin IntersectionObserver
  const handleLoad = (e) => {
    if (loaded) return;
    setLoaded(true);
    onLoad?.(e);
  };

  // Para imágenes no prioritarias, usar rootMargin=200px para precarga temprana
  const imgElement = (
    <img
      ref={imgRef}
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      onLoad={handleLoad}
      className={`w-full h-full transition-opacity duration-500 ${
        loaded ? "opacity-100" : "opacity-0"
      } ${className}`}
      style={{ objectFit, ...rest.style }}
      {...rest}
    />
  );

  // Si es prioritaria (Hero), no usar lazy loading ni placeholder
  if (priority) {
    return (
      <div
        className={`relative overflow-hidden ${containerClassName}`}
        style={{ aspectRatio }}
      >
        {/* Placeholder mínimo que desaparece rápido */}
        <div
          className={`absolute inset-0 bg-[#120F0D] ${
            loaded ? "opacity-0 pointer-events-none" : "opacity-100"
          } transition-opacity duration-300`}
        />
        {imgElement}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden ${containerClassName}`}
      style={{ aspectRatio }}
    >
      {/* Placeholder esqueleto — pulsa mientras carga */}
      <div
        className={`absolute inset-0 bg-[#120F0D] ${
          loaded ? "opacity-0 pointer-events-none" : "opacity-100"
        } transition-opacity duration-500`}
      >
        <div className="w-full h-full animate-pulse bg-gradient-to-br from-[#1A1512] to-[#120F0D]" />
      </div>

      {imgElement}
    </div>
  );
}
